import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import {
  signInWithGoogle,
  signInWithEmail,
  signUpWithEmail,
  sendPasswordReset,
} from '../utils/firebase';
import { trackEvent } from '../utils/analytics';

const MODE_CHOOSE = 'choose';
const MODE_SIGN_IN = 'signin';
const MODE_SIGN_UP = 'signup';
const MODE_RESET = 'reset';

function friendlyAuthError(err) {
  const code = err?.code || '';
  switch (code) {
    case 'auth/invalid-email':
      return 'That email address looks invalid.';
    case 'auth/missing-password':
      return 'Please enter your password.';
    case 'auth/weak-password':
      return 'Password must be at least 6 characters.';
    case 'auth/email-already-in-use':
      return 'An account with that email already exists. Try signing in.';
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return 'Incorrect email or password.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please try again later.';
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return 'Sign-in was cancelled.';
    case 'auth/account-exists-with-different-credential':
      return 'An account with this email exists with a different sign-in method.';
    default:
      return err?.message || 'Something went wrong. Please try again.';
  }
}

function SignInModal({ show, onHide, initialMode = MODE_CHOOSE }) {
  const [mode, setMode] = useState(initialMode);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');

  const resetState = () => {
    setMode(initialMode);
    setLoading(false);
    setError(null);
    setInfo(null);
    setEmail('');
    setPassword('');
    setDisplayName('');
  };

  const handleHide = () => {
    if (loading) return;
    resetState();
    onHide();
  };

  const switchMode = (next) => {
    setMode(next);
    setError(null);
    setInfo(null);
  };

  const runAuth = async (fn) => {
    setLoading(true);
    setError(null);
    setInfo(null);
    try {
      await fn();
      // Success — auth state listener will close the modal or redirect
      resetState();
      onHide();
    } catch (err) {
      setError(friendlyAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    trackEvent('auth_method_clicked', { method: 'google' });
    runAuth(() => signInWithGoogle());
  };

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (mode === MODE_SIGN_IN) {
      trackEvent('auth_method_clicked', { method: 'email_signin' });
      runAuth(() => signInWithEmail(email, password));
    } else if (mode === MODE_SIGN_UP) {
      trackEvent('auth_method_clicked', { method: 'email_signup' });
      runAuth(() => signUpWithEmail(email, password, displayName.trim() || null));
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setInfo(null);
    try {
      await sendPasswordReset(email);
      trackEvent('password_reset_requested');
      setInfo('Password reset email sent. Check your inbox.');
    } catch (err) {
      setError(friendlyAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  const title = (
    mode === MODE_SIGN_UP ? 'Create your account'
      : mode === MODE_RESET ? 'Reset password'
        : 'Sign in to Plum'
  );

  return (
    <Modal show={show} onHide={handleHide} centered backdrop={loading ? 'static' : true}>
      <Modal.Header closeButton={!loading}>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            {error}
          </Alert>
        )}
        {info && (
          <Alert variant="success" onClose={() => setInfo(null)} dismissible>
            {info}
          </Alert>
        )}

        {mode === MODE_CHOOSE && (
          <div className="d-flex flex-column gap-2">
            <Button
              className="auth-provider-btn auth-provider-btn-google"
              onClick={handleGoogle}
              disabled={loading}
            >
              <i className="bi bi-google me-2"></i>
              Continue with Google
            </Button>
            <div className="auth-divider my-2">
              <span>or</span>
            </div>
            <Button
              className="auth-provider-btn auth-provider-btn-email"
              onClick={() => switchMode(MODE_SIGN_IN)}
              disabled={loading}
            >
              <i className="bi bi-envelope me-2"></i>
              Continue with Email
            </Button>
            <p className="text-center mt-2 mb-0" style={{ fontSize: '0.9rem', color: 'var(--text-muted)' }}>
              New to Plum?{' '}
              <Button
                variant="link"
                className="p-0 align-baseline"
                onClick={() => switchMode(MODE_SIGN_UP)}
                disabled={loading}
              >
                Create an account
              </Button>
            </p>
          </div>
        )}

        {(mode === MODE_SIGN_IN || mode === MODE_SIGN_UP) && (
          <Form onSubmit={handleEmailSubmit}>
            {mode === MODE_SIGN_UP && (
              <Form.Group className="mb-3" controlId="signInModalName">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  type="text"
                  autoComplete="name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Your name (optional)"
                  disabled={loading}
                />
              </Form.Group>
            )}
            <Form.Group className="mb-3" controlId="signInModalEmail">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                disabled={loading}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="signInModalPassword">
              <Form.Label>Password</Form.Label>
              <Form.Control
                type="password"
                autoComplete={mode === MODE_SIGN_UP ? 'new-password' : 'current-password'}
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={mode === MODE_SIGN_UP ? 'At least 6 characters' : 'Your password'}
                disabled={loading}
              />
            </Form.Group>
            <Button type="submit" className="cta-btn w-100" disabled={loading}>
              {loading ? (
                <Spinner animation="border" size="sm" />
              ) : mode === MODE_SIGN_UP ? (
                'Create account'
              ) : (
                'Sign in'
              )}
            </Button>
            <div className="d-flex justify-content-between mt-3">
              <Button
                variant="link"
                className="p-0"
                onClick={() => switchMode(MODE_CHOOSE)}
                disabled={loading}
              >
                <i className="bi bi-arrow-left me-1"></i>
                Back
              </Button>
              {mode === MODE_SIGN_IN ? (
                <div className="d-flex gap-3">
                  <Button
                    variant="link"
                    className="p-0"
                    onClick={() => switchMode(MODE_RESET)}
                    disabled={loading}
                  >
                    Forgot password?
                  </Button>
                  <Button
                    variant="link"
                    className="p-0"
                    onClick={() => switchMode(MODE_SIGN_UP)}
                    disabled={loading}
                  >
                    Sign up
                  </Button>
                </div>
              ) : (
                <Button
                  variant="link"
                  className="p-0"
                  onClick={() => switchMode(MODE_SIGN_IN)}
                  disabled={loading}
                >
                  Already have an account?
                </Button>
              )}
            </div>
          </Form>
        )}

        {mode === MODE_RESET && (
          <Form onSubmit={handleReset}>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem' }}>
              Enter your email and we&rsquo;ll send you a link to reset your password.
            </p>
            <Form.Group className="mb-3" controlId="signInModalResetEmail">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                disabled={loading}
              />
            </Form.Group>
            <Button type="submit" className="cta-btn w-100" disabled={loading}>
              {loading ? <Spinner animation="border" size="sm" /> : 'Send reset email'}
            </Button>
            <div className="mt-3">
              <Button
                variant="link"
                className="p-0"
                onClick={() => switchMode(MODE_SIGN_IN)}
                disabled={loading}
              >
                <i className="bi bi-arrow-left me-1"></i>
                Back to sign in
              </Button>
            </div>
          </Form>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default SignInModal;
